// src/services/userService.js
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { db, auth } from "../firebase";

export async function getUserData(uid) {
  try {
    const userRef = doc(db, "Users", uid);
    const userSnap = await getDoc(userRef);

    const baseFallback = {
      userDisplayName: "null-error",
      userPortfolioValue: 0,
      userMoney: 0,
      userXP: 0,
      userCEO: false,
      userRank: 1,
      userLevel: "Basic",
      userCompanyTicker: "NONE",
      userRegion: "NONE",
    };

    if (userSnap.exists()) {
      const data = userSnap.data();
      return {
        ...baseFallback,
        ...data,
        userDisplayName: data.userDisplayName ?? "null-error",
        userPortfolioValue: data.userPortfolioValue ?? 0,
        userMoney: data.userMoney ?? 0,
        userXP: data.userXP ?? 0,
        userCEO: data.userCEO ?? false,
        userRank: data.userRank ?? 1,
        userLevel: data.userLevel ?? "Basic",
        userCompanyTicker: data.userCompanyTicker ?? "NONE",
        userRegion: data.userRegion ?? "NONE",
      };
    }

    console.warn(`User document for ${uid} does not exist.`);
    return baseFallback;

  } catch (error) {
    console.error("Error fetching user data:", error);
    return {
      userDisplayName: "null-error",
      userPortfolioValue: 0,
      userMoney: 0,
      userXP: 0,
      userCEO: false,
      userRank: 1,
      userLevel: "Basic",
      userCompanyTicker: "NONE",
      userRegion: "NONE",
    };
  }
}

export function listenToUserData(uid, callback) {
  const userRef = doc(db, "Users", uid);

  let prevData = null;

  const baseFallback = {
    userDisplayName: "null-error",
    userPortfolioValue: 0,
    userMoney: 0,
    userXP: 0,
    userCEO: false,
    userRank: 1,
    userLevel: "Basic",
    userCompanyTicker: "NONE",
    userRegion: "NONE",
  };

  const unsubscribe = onSnapshot(
    userRef,
    (docSnap) => {
      const exists = docSnap.exists();
      const raw = exists ? docSnap.data() : baseFallback;

      const normalized = {
        ...baseFallback,
        ...raw,
        userDisplayName: raw.userDisplayName ?? "null-error",
        userPortfolioValue: raw.userPortfolioValue ?? 0,
        userMoney: raw.userMoney ?? 0,
        userXP: raw.userXP ?? 0,
        userCEO: raw.userCEO ?? false,
        userRank: raw.userRank ?? 1,
        userLevel: raw.userLevel ?? "Basic",
        userCompanyTicker: raw.userCompanyTicker ?? "NONE",
        userRegion: raw.userRegion ?? "NONE",
      };

      let rankUp = false;
      let previousRank = null;

      if (prevData) {
        previousRank = prevData.userRank ?? 1;
        const currentRank = normalized.userRank ?? 1;

        if (currentRank > previousRank) {
          rankUp = true;
        }
      }

      prevData = normalized;

      callback({
        ...normalized,
        _meta: {
          rankUp,
          previousRank: prevData?.userRank ?? 1,
          currentRank: normalized.userRank ?? 1,
        },
      });
    },
    (error) => {
      console.error("Error listening to user data:", error);
    }
  );

  return unsubscribe;
}


export async function createUserData(uid, data) {
  try {
    const userRef = doc(db, "Users", uid);
    await setDoc(userRef, data, { merge: true });
    console.log(`User data created/updated for ${uid}`);
  } catch (error) {
    console.error("Error creating/updating user data:", error);
  }
}

export async function getUserDisplayName(uid) {
  try {
    const userRef = doc(db, "Users", uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data().userDisplayName ?? "null-error";
    }
    return "null-error";
  } catch (error) {
    console.error("Error fetching display name:", error);
    return "null-error";
  }
}

export async function getUserCharts() {
  try {
    if (!auth.currentUser) throw new Error("User not authenticated");
    const idToken = await auth.currentUser.getIdToken();

    const res = await fetch(
      "https://getusercharts-5dhfwyjvzq-uc.a.run.app",
      {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Failed to fetch user charts: ${errText}`);
    }

    const data = await res.json();
    return data;

  } catch (error) {
    console.error("Error fetching user charts:", error);
    return {
      userChart7D: [],
      userChart30D: [],
      userChart90D: [],
    };
  }
}